import React, { ChangeEvent, Fragment, useEffect } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import aa from 'search-insights';

import backArrowIcon from '@/assets/icons/auto-complete-back-left-icon.svg';
import RightIcon from '@/assets/images/arrow-right-gray.svg';
import LinkIcon from '@/assets/images/arrow-up-left.svg';
import GridIcon from '@/assets/images/grid-icon.svg';
import LinkExternalIcon from '@/assets/images/link-external-icon.svg';
import RefreshIcon from '@/assets/images/refresh-icon.svg';
import SearchIcon from '@/assets/images/search-icon-black.svg';
import ProductImg2 from '@/assets/images/search-product2.png';
import Button from '@/components/V2/Button';
import Rating from '@/components/V2/Rating';
import { ALGOLIA_INDEX_NAME } from '@/config/env';
import { useWindowDimensions } from '@/helpers/hooks';
import useEzShip from '@/helpers/hooks/useEzShip';
import useWebAlias from '@/helpers/hooks/useWebalias';
import cn from '@/helpers/utils/classNames';
import { loadApproperiateImage } from '@/helpers/utils/cloudflare';
import { getRelativeUrlForInternalDomains } from '@/helpers/utils/urlHelpers';
import { useAppContext } from '@/layouts/AppProvider';
import { useAppSelector } from '@/store';
import { AlgoliaProduct } from '@/types/Algolia';
import { AutocompleteCollection } from '@algolia/autocomplete-js';
import { RedirectUrlItem } from '@algolia/autocomplete-plugin-redirect-url';

import PopularCategories from './PopularCategories';
import RecentSearchesContainer from './RecentSearchesContainer';
import { QuerySuggestion, useSearchContext } from './SearchBarProvider';

type AutoCompeleteProps = {
  className?: string;
  containerClassName?: string;
  isDashboard?: boolean;
  isMobileDashboard?: boolean;
};

const PopularSearchesComp = ({
  popularSearches,
  onClick: onClickPopularSearch,
}: {
  popularSearches: AutocompleteCollection<QuerySuggestion>;
  onClick: () => void;
}) => {
  const { onSearchChange, getInputProps, setSearchBarFocused } = useSearchContext();
  const { onChange, onCompositionEnd, onFocus, type, onKeyDown, onClick, ...inputProps } = getInputProps({
    inputElement: null,
  });

  return (
    <>
      {popularSearches?.items?.length > 0 && (
        <>
          <h3 className="v2-font-medium v2-font-sweet-sans-pro-medium v2-text-[#101828] v2-text-sm v2-mb-[12px]">
            Popular Searches
          </h3>
          <div className="v2-flex v2-gap-[12px] v2-items-center v2-flex-wrap">
            {popularSearches.items.map(search => (
              <div
                key={search.query}
                className="v2-px-2 v2-py-[2px] v2-cursor-pointer v2-text-[#344054] v2-font-sweet-sans-pro v2-font-normal v2-text-xs v2-border v2-border-[#EAECF0] v2-bg-[#F9FAFB] v2-rounded-xs"
                onClick={e => {
                  e.preventDefault();
                  onClickPopularSearch();
                  const target = { value: search.query };
                  const event = { target, currentTarget: target, relatedTarget: target };

                  onChange(event as unknown as Event);
                  onSearchChange(event as unknown as ChangeEvent<HTMLInputElement>);

                  // setSearchBarFocused(false);
                }}
              >
                <p className="">{search.query}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};
const PopularSearches = React.memo(PopularSearchesComp);

const AlgoliaAutoComplete = ({ className, containerClassName, isDashboard, isMobileDashboard }: AutoCompeleteProps) => {
  const { isBackOffice } = useAppContext();
  const router = useRouter();
  const { webalias, generateURL } = useWebAlias();
  const { ezShipId, generateURL: generateEzShipURL } = useEzShip();

  const {
    value: searchQuery,
    autocompleteState,
    closeAutoCompleteMenu,
    algoliaCategories,
    setSearchBarFocused,
    setFocusedCategory,
    focusedCategory,
    algoliaProductsForFocusedCategory,
    quickAccessList,
    isQuickAccessListLoading,
    algoliaProductsForSearchQuery,
    blurInputFields,
    onSearch,
    deadStateRef,
    addSearchQueryToRecentSearches,
  } = useSearchContext();
  const { onSearchChange, getInputProps } = useSearchContext();
  const { onChange, onCompositionEnd, onFocus, type, onKeyDown, onClick, ...inputProps } = getInputProps({
    inputElement: null,
  });

  const cookieConsent = useAppSelector(state => state.slices.appConfig.isCookieConsentRejected);

  const searchTerm = autocompleteState.query?.trim();
  const hasQuery = !!searchTerm && searchQuery;
  const redirectUrlsCollection = autocompleteState.collections.find(
    c => c.source.sourceId === 'redirectUrlPlugin'
  ) as AutocompleteCollection<RedirectUrlItem>;

  const querySuggestionsCollection = autocompleteState.collections.find(
    c => c.source.sourceId === 'querySuggestionsPlugin'
  ) as AutocompleteCollection<QuerySuggestion>;

  const algoliaProductsCollection = autocompleteState.collections.find(
    c => c.source.sourceId === 'algoliaProducts'
  ) as AutocompleteCollection<AlgoliaProduct>;

  const products =
    focusedCategory.length && algoliaCategories?.length
      ? algoliaProductsForFocusedCategory?.hits || []
      : algoliaProductsCollection?.items || [];

  const popularSearches = autocompleteState.collections.find(
    c => c.source.sourceId === 'popularPlugin'
  ) as AutocompleteCollection<QuerySuggestion>;

  const querySuggestions =
    querySuggestionsCollection?.items?.filter(item => (isBackOffice ? false : !item?.query?.includes('proshop'))) || [];

  const categoryIdsInProductsWithAPossibleMatch = products.map(
    p => p?.Categories?.find(c => c.Name === focusedCategory && !!c.BackofficeCategory == isBackOffice)
  );

  const categoryIdForGoToCategoryButton = categoryIdsInProductsWithAPossibleMatch.find(p => !!p?.id);

  const showGoToCategoryButton = !!(focusedCategory.length && products?.length && categoryIdForGoToCategoryButton?.id);
  const seeAllProductsButton = !focusedCategory.length && products?.length > 0;
  const getProductUrl = (product: AlgoliaProduct) => {
    const slug = product.ParentGMItem?.Slug || product.GMItem?.Slug || product.Slug;
    const itemCode =
      product.ParentGMItem?.ItemCode ||
      product.GMItem?.ItemCode ||
      product.ParentGMCode ||
      product.GMCode ||
      product.ItemCode;

    const addQueryParamsInUrl =
      focusedCategory.length ||
      product?.__autocomplete_queryID ||
      product.ParentGMItem?.ItemCode ||
      product.GMItem?.ItemCode;
    const urlQueryParams = new URLSearchParams({
      ...(product.ParentGMItem?.ItemCode ? { itemCode: product.GMItem?.ItemCode } : {}),
      ...(product.ParentGMItem?.ItemCode && product.GMItem?.ItemCode ? { innerItemCode: product?.ItemCode } : {}),
      ...(product.GMItem?.ItemCode && !product.ParentGMItem?.ItemCode ? { itemCode: product.ItemCode } : {}),
      ...(focusedCategory.length || product?.__autocomplete_queryID
        ? { queryID: algoliaProductsForFocusedCategory?.queryID || product?.__autocomplete_queryID || '' }
        : {}),
    });
    return generateURL(
      generateEzShipURL(`/product/${encodeURIComponent(slug || itemCode)}`, ezShipId) +
        (addQueryParamsInUrl ? '?' : '') +
        urlQueryParams.toString(),
      isBackOffice ? 'shopping' : webalias
    );
  };

  const trackProductClickEventForAlgolia = (product: AlgoliaProduct, idx: number) => {
    if (cookieConsent !== true) return;
    const queryId = focusedCategory.length
      ? algoliaProductsForFocusedCategory?.queryID
      : product?.__autocomplete_queryID;
    const event = queryId?.length ? 'clickedObjectIDsAfterSearch' : 'clickedObjectIDs';
    const itemCode = product.ItemCode;
    aa('getUserToken', {}, (err, userToken) => {
      if (err) {
        console.error(err);
        return;
      }

      aa('getAuthenticatedUserToken', {}, (err, authenticatedUserToken) => {
        if (err) {
          console.error(err, 'cannot get getAuthenticatedUserToken');
          return;
        }
        aa(event, {
          userToken: `${userToken || ''}`,
          authenticatedUserToken: `${authenticatedUserToken || ''}`,
          index: ALGOLIA_INDEX_NAME,
          eventName: 'Product Clicked',
          ...(queryId?.length ? { queryID: queryId, positions: [idx] } : {}),
          objectIDs: [itemCode],
        });
      });
    });
  };

  useEffect(() => {
    if (!algoliaCategories.length) {
      setFocusedCategory('');
    }
  }, [algoliaCategories, setFocusedCategory]);

  const showNoResultsState =
    !!autocompleteState.query &&
    !querySuggestions?.length &&
    !redirectUrlsCollection?.items?.length &&
    !algoliaProductsCollection?.items?.length &&
    autocompleteState?.status == 'idle';

  const isNoResultsStateVisible = showNoResultsState;

  deadStateRef.current = isNoResultsStateVisible;

  const isLoading = autocompleteState.status === 'loading';

  const focusInputField = () => {
    function isElementInViewport(el: Element): boolean {
      const rect = el.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    }
    const input = document.getElementById('search-from-menubar');
    if (input && isElementInViewport(input)) {
      input.focus();
    }

    const mainSearchInput = document.getElementById('search-from-header');
    if (mainSearchInput && isElementInViewport(mainSearchInput)) {
      mainSearchInput.focus();
    }
  };

  const getQueryTitle = (item: RedirectUrlItem) => {
    const query = redirectUrlsCollection.source.getItemInputValue({
      item,
      state: autocompleteState as any,
    });

    return query.toLowerCase().includes('pet') && query.toLowerCase().includes('profile')
      ? query.replace('Profile', 'Quiz').replace('profile', 'quiz')
      : query;
  };

  const screenWidth = useWindowDimensions().width;

  // console.log(algoliaProductsCollection, 'algoliaProductsForSearchQuery');

  return (
    <div
      className={cn(
        'v2-absolute v2-inset-0 v2-bg-white v2-top-[46px] v2-min-h-fit v2-z-[9999] v2-h-full v2-w-full v2-shadow-lg',
        {
          containerClassName,
          '!v2-max-w-[1080px]': isDashboard,
          'v2-top-[130px]': isMobileDashboard,
        },
        '__autocomplete-search-results'
      )}
      onBlur={() => setSearchBarFocused(false)}
    >
      <div
        className={cn('v2-max-w-[1080px] v2-mx-auto v2-w-full v2-relative', {
          '!v2-mx-0 v2-h-auto xs:v2-h-[85dvh] v2-overflow-auto v2-scrollbar-none': isDashboard,
        })}
      >
        <div className="v2-p-[24px] md:v2-p-[24px] sm:v2-p-3 xs:v2-p-3 v2-shadow-md v2-rounded-xs v2-flex v2-flex-row md:v2-flex-row sm:v2-flex-col xs:v2-flex-col v2-items-start v2-gap-[24px] v2-max-h-[424px] md:v2-max-h-[424px] sm:v2-max-h-fit xs:v2-max-h-fit">
          {/* Quick Access */}
          {!hasQuery && (
            <>
              <div className="v2-w-full v2-max-w-[322px] md:v2-max-w-[322px] sm:v2-max-w-full xs:v2-max-w-full v2-h-[376px] md:v2-h-[376px] sm:v2-h-auto xs:v2-h-auto custom-scroll v2-overflow-hidden v2-overflow-y-scroll">
                <RecentSearchesContainer />

                <PopularSearches popularSearches={popularSearches} onClick={() => focusInputField()} />
              </div>
              <div className="v2-w-full">
                <h3 className="v2-font-medium v2-font-sweet-sans-pro-medium v2-text-[#101828] v2-text-sm v2-mb-[12px]">
                  Quick Access
                </h3>
                {isQuickAccessListLoading ? (
                  <div className="v2-grid v2-animate-pulse v2-grid-cols-4 md:v2-grid-cols-4 sm:v2-grid-cols-2 xs:v2-grid-cols-2 v2-gap-[16px]">
                    {[1, 2, 3, 4].map(item => (
                      <div key={item} className="v2-w-full v2-h-[270px] v2-bg-gray-200 v2-rounded-xs" />
                    ))}
                  </div>
                ) : (
                  <div className="v2-grid v2-grid-cols-4 md:v2-grid-cols-4 sm:v2-grid-cols-2 xs:v2-grid-cols-2 v2-gap-[16px]">
                    {quickAccessList.map((item, index) => (
                      <ConditionallyRenderComponent
                        link={
                          getRelativeUrlForInternalDomains(item?.URL?.value || '', isBackOffice).replace(
                            '{0}',
                            webalias
                          ) ?? ''
                        }
                        key={index}
                      >
                        {item?.GreenBackgroundSection && item?.GreenBackgroundSection?.visible ? (
                          <div
                            className={cn(
                              'v2-rounded-xs v2-bg-[#F4F5F5] v2-min-h-[160px] v2-h-auto v2-flex v2-items-center v2-flex-col v2-justify-around v2-relative',
                              `order-[${item.Order}]`
                            )}
                          >
                            <Image
                              src={loadApproperiateImage(item.DesktopAndTabletImageURL, 'hd')}
                              alt=""
                              width={270}
                              height={160}
                              quality={100}
                              // className="!v2-w-fit"
                            />
                            <div className="v2-bg-primary v2-p-[12px] v2-absolute v2-bottom-0 v2-w-full v2-rounded-b-xs v2-h-[62px] v2-flex v2-justify-center v2-items-center">
                              <div
                                className="v2-text-center quick-access-sale-container"
                                dangerouslySetInnerHTML={{ __html: item?.GreenBackgroundSection?.value ?? '' }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div
                            className={cn(
                              'v2-rounded-xs v2-bg-[#F4F5F5] v2-min-h-[160px] v2-h-auto   v2-text-center v2-flex v2-flex-col v2-items-center v2-justify-between v2-gap-2',
                              `order-[${item.Order}]`,
                              { 'v2-px-[12px] v2-pb-[10px] v2-pt-[12px]': item?.TopSection?.visible }
                            )}
                          >
                            {item?.TopSection?.visible && (
                              <p
                                className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-xs"
                                dangerouslySetInnerHTML={{ __html: item?.TopSection?.value || '' }}
                              />
                            )}
                            <Image
                              src={loadApproperiateImage(item.DesktopAndTabletImageURL, 'hd')}
                              alt=""
                              width={270}
                              height={160}
                              quality={100}
                              className="v2-my-auto"
                            />
                            {item?.BottomSection?.visible && (
                              <p
                                className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-xs"
                                dangerouslySetInnerHTML={{ __html: item?.BottomSection?.value || '' }}
                              />
                            )}
                          </div>
                        )}
                      </ConditionallyRenderComponent>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {/* When click on search bar */}

          {hasQuery && !isNoResultsStateVisible && (
            <>
              <div className="v2-w-full v2-max-w-[322px] md:v2-max-w-[322px] sm:v2-max-w-full xs:v2-max-w-full v2-h-[376px] md:v2-h-[376px] sm:v2-h-auto xs:v2-h-[376px] custom-scroll v2-overflow-hidden v2-overflow-y-scroll v2-pr-1">
                <h3 className="v2-font-medium v2-font-sweet-sans-pro-medium v2-text-[#101828] v2-text-sm v2-mb-[12px]">
                  Suggestions
                </h3>
                <div
                  className={cn('v2-mb-[24px] v2-flex v2-flex-col', {
                    'v2-animate-pulse': isLoading,
                  })}
                >
                  {isLoading && (
                    <>
                      <div className="v2-mb-[24px] v2-flex v2-flex-col v2-gap-2">
                        <div className="v2-flex v2-gap-[8px] v2-items-center v2-h-2.5 v2-bg-gray-200 v2-w-[90%] v2-justify-center v2-animate-pulse">
                          <p className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-sm v2-w-full"></p>
                        </div>
                      </div>
                      <div className="v2-mb-[24px] v2-flex v2-flex-col v2-gap-2">
                        <div className=" v2-flex v2-gap-[8px] v2-items-center v2-h-2.5 v2-bg-gray-200 v2-w-[90%] v2-justify-center v2-animate-pulse">
                          <p className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-sm v2-w-full"></p>
                        </div>
                      </div>
                      <div className="v2-mb-[24px] v2-flex v2-flex-col v2-gap-2">
                        <div className="v2-flex v2-gap-[8px] v2-items-center v2-h-2.5 v2-bg-gray-200 v2-w-[90%] v2-justify-center v2-animate-pulse">
                          <p className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-sm v2-w-full"></p>
                        </div>
                      </div>
                      <div className="v2-mb-[24px] v2-flex v2-flex-col v2-gap-2">
                        <div className="v2-flex v2-gap-[8px] v2-items-center v2-h-2.5 v2-bg-gray-200 v2-w-[90%] v2-justify-center v2-animate-pulse">
                          <p className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-sm v2-w-full"></p>
                        </div>
                      </div>
                      <div className="v2-mb-[24px] v2-flex v2-flex-col v2-gap-2">
                        <div className="v2-flex v2-gap-[8px] v2-items-center v2-h-2.5 v2-bg-gray-200 v2-w-[90%] v2-justify-center v2-animate-pulse">
                          <p className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-sm v2-w-full"></p>
                        </div>
                      </div>
                    </>
                  )}
                  {algoliaProductsCollection?.items?.map(item => (
                    <div
                      key={item.objectID}
                      className="v2-flex v2-gap-[8px] v2-items-center v2-py-[8px] v2-cursor-pointer hover:v2-bg-[#F9FAFB]"
                      onMouseEnter={() => setFocusedCategory('')}
                    >
                      <Image src={RefreshIcon} alt="" width={16} height={16} />
                      <p className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-sm v2-w-full">
                        <span
                          key={item.ItemCode}
                          onClick={e => {
                            e.preventDefault();

                            const target = { value: item.ItemName };
                            const event = { target, currentTarget: target, relatedTarget: target };
                            onSearchChange(event as unknown as ChangeEvent<HTMLInputElement>);
                            setSearchBarFocused(false);
                            onSearch(item.ItemName);
                            blurInputFields();
                            // addSearchQueryToRecentSearches(item.ItemName);
                          }}
                          className="v2-text-sm"
                        >
                          <span
                            dangerouslySetInnerHTML={{
                              __html: (item?._highlightResult?.ItemName?.value || item.ItemName)
                                .replaceAll('__aa-highlight__', '<b>')
                                .replaceAll('__/aa-highlight__', '</b>'),
                            }}
                          ></span>
                        </span>
                        {item.IsOutOfStock && <span className="v2-text-red-700"> (Out of Stock)</span>}
                      </p>
                      <Image
                        src={LinkIcon}
                        width={16}
                        height={16}
                        alt=""
                        onClick={e => {
                          e.preventDefault();
                          focusInputField();
                          const target = { value: item.ItemName };
                          const event = { target, currentTarget: target, relatedTarget: target };

                          onChange(event as unknown as Event);
                          onSearchChange(event as unknown as ChangeEvent<HTMLInputElement>);

                          // blurInputFields();
                          // setImmediate(() => focusInputField());
                        }}
                      />
                    </div>
                  ))}

                  {querySuggestions?.map?.(item => (
                    <div
                      key={item.objectID}
                      className="v2-flex v2-gap-[8px] v2-items-center v2-py-[8px] v2-cursor-pointer hover:v2-bg-[#F9FAFB]"
                      onMouseEnter={() => setFocusedCategory('')}
                    >
                      <Image src={SearchIcon} alt="" width={16} height={16} />
                      <p
                        className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-sm"
                        onClick={e => {
                          e.preventDefault();

                          const target = { value: item.query };
                          const event = { target, currentTarget: target, relatedTarget: target };
                          closeAutoCompleteMenu();
                          setSearchBarFocused(false);
                          onSearchChange(event as unknown as ChangeEvent<HTMLInputElement>);
                          onSearch(item.query);
                          blurInputFields();
                          // addSearchQueryToRecentSearches(item.query);
                        }}
                      >
                        {item.query}
                      </p>
                      <Image
                        src={LinkIcon}
                        width={16}
                        height={16}
                        alt=""
                        className="v2-ml-auto"
                        onClick={e => {
                          e.preventDefault();
                          focusInputField();
                          const target = { value: item.query };
                          const event = { target, currentTarget: target, relatedTarget: target };

                          onChange(event as unknown as Event);
                          onSearchChange(event as unknown as ChangeEvent<HTMLInputElement>);
                          // blurInputFields();
                          // setImmediate(() => focusInputField());
                        }}
                      />
                    </div>
                  ))}

                  {algoliaCategories.map((cat, idx) => (
                    <Fragment key={`categories-${idx}`}>
                      {cat.facetHits.map(facet => (
                        <div
                          key={facet.highlighted}
                          className="v2-flex v2-gap-[8px] v2-items-center v2-py-[8px] v2-cursor-pointer hover:v2-bg-[#F9FAFB]"
                          onMouseEnter={() => setFocusedCategory(facet.value)}
                        >
                          <Image src={GridIcon} alt="" width={16} height={16} />
                          <p className="v2-font-normal v2-font-sweet-sans-pro v2-text-[#101828] v2-text-sm">
                            {facet.value}
                          </p>
                          <Image src={RightIcon} width={16} height={16} alt="" className="v2-ml-auto" />
                        </div>
                      ))}
                    </Fragment>
                  ))}

                  {!!redirectUrlsCollection?.items?.length && (
                    <>
                      {(redirectUrlsCollection?.items).map((item, idx) => (
                        <Fragment key={`redirect-${idx}`}>
                          {item.sourceId == 'algoliaProducts' ? (
                            item.urls.map(url => (
                              <div key={url} className="v2-flex v2-gap-[8px] v2-items-center">
                                <Link
                                  href={getRelativeUrlForInternalDomains(url, isBackOffice).replace('{0}', webalias)}
                                  onClick={e => {
                                    e.preventDefault();
                                    addSearchQueryToRecentSearches(
                                      getQueryTitle(item),
                                      true,
                                      getRelativeUrlForInternalDomains(url, isBackOffice)
                                    );
                                    router.push(
                                      getRelativeUrlForInternalDomains(url, isBackOffice).replace('{0}', webalias)
                                    );
                                  }}
                                  className="v2-flex v2-items-center v2-gap-2 v2-font-normal v2-font-sweet-sans-pro v2-text-primary-700 v2-text-sm v2-underline hover:v2-no-underline v2-py-[8px]"
                                >
                                  <Image src={LinkExternalIcon} alt="" />{' '}
                                  <span className="v2-capitalize">{getQueryTitle(item)}</span>
                                </Link>
                              </div>
                            ))
                          ) : (
                            <></>
                          )}
                        </Fragment>
                      ))}
                    </>
                  )}
                </div>
              </div>
              <div
                className={cn('v2-w-full', {
                  'md:v2-relative v2-absolute v2-right-3 v2-left-3 v2-bg-white v2-z-[2px] !v2-w-auto v2-h-full v2-overflow-auto v2-bottom-2':
                    focusedCategory,
                })}
              >
                <h3
                  className={cn(
                    'v2-font-medium v2-font-sweet-sans-pro-medium v2-text-[#101828] v2-text-sm v2-mb-[12px]',
                    {
                      hidden: !products?.length,
                    },
                    {
                      hidden: focusedCategory,
                    }
                  )}
                >
                  Products
                </h3>
                {focusedCategory && (
                  <div
                    className="md:v2-hidden v2-flex v2-gap-[6px] v2-my-6"
                    role="button"
                    onClick={() => setFocusedCategory('')}
                  >
                    <Image src={backArrowIcon} alt="back arrow" />
                    <p className="v2-font-sweet-sans-pro-medium v2-font-medium v2-text-sm v2-text-[#123E2E]">Back</p>
                  </div>
                )}
                <div>
                  <div
                    className={cn(
                      'v2-grid v2-grid-cols-4 sm:v2-grid-cols-2 md:v2-grid-cols-4 xs:v2-grid-cols-2 v2-gap-[12px]',
                      { '!v2-grid-cols-1': focusedCategory && screenWidth <= 767 }
                    )}
                  >
                    {products?.slice?.(0, 4)?.map?.((product, idx) => (
                      <div
                        key={product.objectID}
                        className="v2-p-2 v2-cursor-pointer"
                        onClick={() => {
                          const productUrl = getProductUrl(product);
                          setSearchBarFocused(false);
                          addSearchQueryToRecentSearches(
                            product.GMItem?.ItemName || product.ItemName,
                            true,
                            productUrl
                          );
                          trackProductClickEventForAlgolia(product, idx + 1);
                          router.push(productUrl);
                        }}
                      >
                        <div className="v2-bg-[#F3F6F5] v2-rounded-xs v2-p-[14px] v2-h-[198px] md:v2-h-[110px] v2-flex v2-items-center v2-justify-center v2-mb-2 v2-relative">
                          <Image
                            src={product?.MainImage ? loadApproperiateImage(product?.MainImage, 'public') : ProductImg2}
                            height={293}
                            width={293}
                            alt=""
                            layout="responsive"
                            quality={100}
                            className="!v2-w-auto !v2-h-[198px] md:!v2-h-[116px]"
                          />
                          <div className="v2-absolute v2-left-1 v2-bottom-1 v2-flex-col v2-gap-1 v2-hidden">
                            <div className="v2-px-2 v2-py-[2px] v2-text-[#344054] v2-font-sweet-sans-pro v2-font-normal v2-text-xs v2-border v2-border-[#EAECF0] v2-bg-[#F9FAFB] v2-rounded-xs">
                              <p className="">Label</p>
                            </div>
                            <div className="v2-px-2 v2-py-[2px] v2-text-[#026AA2] v2-font-sweet-sans-pro v2-font-normal v2-text-xs v2-border v2-border-[#B9E6FE] v2-bg-[#F0F9FF] v2-rounded-xs">
                              <p className="">Label</p>
                            </div>
                          </div>
                        </div>
                        <div className="v2-flex v2-flex-col v2-gap-1">
                          <h5 className="v2-font-medium v2-font-sweet-sans-pro-medium v2-text-[#23263B] v2-text-sm">
                            {product.GMItem?.ItemName || product.ItemName}
                          </h5>
                          <div className="v2-font-normal v2-font-sweet-sans-pro v2-text-primary v2-text-xs v2-flex v2-items-center v2-gap-[6px]">
                            <p>{product?.GMItem?.Price || product?.Price}</p>
                            {(product?.GMItem?.HasStrikeOutPrice || product?.HasStrikeOutPrice) && (
                              <p className="v2-text-[#D92D20] v2-line-through">
                                {' '}
                                {product?.GMItem?.StrikeOutPrice || product?.StrikeOutPrice}
                              </p>
                            )}
                          </div>

                          <div>
                            <span
                              key={product?.rating}
                              className="v2-font-sweet-sans-pro v2-gap-1 v2-capitalize v2-font-normal v2-text-xs v2-text-neutral-[#101828] v2-overflow-hidden v2-text-ellipsis v2-whitespace-nowrap v2-flex v2-items-center"
                            >
                              {/* Filled Icons */}

                              {Array.from({ length: product?.rating }, (_, i) => i + 1).map((_, index) => (
                                <Rating
                                  height={16}
                                  width={16}
                                  key={`rating-${product?.rating}-key-${_}`}
                                  isFilled={true}
                                />
                              ))}
                              {/* Shallow Icons */}
                              {Array.from({ length: 5 - product?.rating || 0 }, (_, i) => i + 1).map((_, index) => (
                                <Rating
                                  height={16}
                                  width={16}
                                  key={`rating-${product?.rating}-key-${_}`}
                                  isFilled={false}
                                />
                              ))}

                              {!!product?.totalReviews && ` (${product?.totalReviews || ''})`}
                            </span>
                          </div>

                          {!!product?.PromotionFeature?.length && (
                            <div className="v2-px-2 v2-py-[2px] v2-text-[#B42318] v2-font-sweet-sans-pro v2-font-normal v2-text-xs v2-border v2-border-[#FECDCA] v2-bg-[#FEF3F2] v2-rounded-xs v2-w-fit">
                              <p className="" dangerouslySetInnerHTML={{ __html: product?.PromotionFeature || '' }}></p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {showGoToCategoryButton && (
                    <div className="v2-mt-6">
                      <Button
                        as="Link"
                        btnSize="sm"
                        scheme="primary"
                        type="button"
                        variant="filled"
                        className="v2-px-[14px] v2-font-sweet-sans-pro-medium v2-font-medium md:v2-text-base sm:v2-text-xs xs:v2-text-xs v2-text-center v2-w-fit md:v2-w-fit sm:v2-w-full xs:v2-w-full v2-break-all v2-whitespace-pre-wrap"
                        onClick={() => {
                          addSearchQueryToRecentSearches(
                            focusedCategory,
                            true,
                            ezShipId
                              ? generateURL(
                                  `${generateEzShipURL(
                                    `/products/${categoryIdForGoToCategoryButton.id}`,
                                    ezShipId
                                  )}&query=${searchQuery || autocompleteState.query}`,
                                  webalias
                                )
                              : `${generateURL('/products/' + categoryIdForGoToCategoryButton.id, webalias)}?query=${
                                  searchQuery || autocompleteState.query
                                }`
                          );
                          setSearchBarFocused(false);
                          setFocusedCategory('');
                        }}
                        href={
                          ezShipId
                            ? generateURL(
                                `${generateEzShipURL(
                                  `/products/${categoryIdForGoToCategoryButton.id}`,
                                  ezShipId
                                )}&query=${searchQuery || autocompleteState.query}`,
                                webalias
                              )
                            : `${generateURL('/products/' + categoryIdForGoToCategoryButton.id, webalias)}?query=${
                                searchQuery || autocompleteState.query
                              }`
                        }
                      >
                        See All Results in {focusedCategory} ({algoliaProductsForFocusedCategory?.nbHits})
                      </Button>
                    </div>
                  )}
                  {seeAllProductsButton && (
                    <div className="v2-mt-6">
                      <Button
                        btnSize="sm"
                        scheme="primary"
                        type="button"
                        variant="filled"
                        className="v2-px-[14px] v2-font-sweet-sans-pro-medium v2-font-medium md:v2-text-base sm:v2-text-xs xs:v2-text-xs v2-text-center v2-w-fit md:v2-w-fit sm:v2-w-full xs:v2-w-full v2-break-all v2-whitespace-pre-wrap"
                        onClick={() => {
                          setSearchBarFocused(false);
                          setFocusedCategory('');
                          onSearch();
                        }}
                      >
                        See All Products
                        {/* ({algoliaProductsForSearchQuery?.nbHits}) */}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {/* When Search */}
          {isNoResultsStateVisible && (
            <div>
              <div className="v2-mb-6 v2-font-sweet-sans-pro-medium v2-font-medium v2-text-[#23263B] v2-text-base">
                <p>Sorry, we didn’t find any matches for “{searchQuery || autocompleteState.query}”</p>
              </div>
              <div className="v2-flex v2-items-start v2-flex-row md:v2-flex-row sm:v2-flex-col xs:v2-flex-col v2-gap-6">
                <div className="v2-w-full v2-max-w-[322px] md:v2-max-w-[322px] sm:v2-max-w-full xs:v2-max-w-full v2-h-[376px] md:v2-h-[376px] sm:v2-h-auto xs:v2-h-auto custom-scroll v2-overflow-hidden v2-overflow-y-scroll">
                  <h3 className="v2-font-medium v2-font-sweet-sans-pro-medium v2-text-[#101828] v2-text-sm v2-mb-[12px]">
                    Try the following:
                  </h3>
                  <div className="v2-mb-[24px] v2-flex v2-flex-col">
                    <ul className="v2-font-sweet-sans-pro v2-font-normal v2-text-sm v2-text-[#23263B] v2-list-disc v2-ml-[22px]">
                      <li>Double check your spelling</li>
                      <li>Use fewer keywords</li>
                      <li>Search for a less specific item and refine the results</li>
                    </ul>
                  </div>

                  <PopularSearches popularSearches={popularSearches} onClick={() => focusInputField()} />
                </div>

                <PopularCategories />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlgoliaAutoComplete;

// export default AutoComplete;

const ConditionallyRenderComponent = ({ children, link }: { children: React.ReactNode; link: string }) => {
  if (link) {
    return <Link href={link}>{children}</Link>;
  }
  return children;
};
